import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
  IconButton,
  Typography
} from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import { debounce } from 'lodash';

/**
 * Filter component for employee list with search and filter options
 * @param {Object} props - Component props
 * @param {Function} props.onFilterChange - Handler for filter changes
 * @param {Object} props.filters - Current filter state
 * @returns {React.Component} Filter UI with search and filter controls
 */
function EmployeeFilters({ onFilterChange, filters = {} }) {
  const [searchText, setSearchText] = useState(filters.search || '');

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      onFilterChange({ ...filters, search: value });
    }, 300),
    [filters]
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  const handleClearSearch = () => {
    setSearchText('');
    onFilterChange({ ...filters, search: '' });
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters };
    
    if (value === '' || value === null || value === undefined) {
      delete newFilters[field];
    } else {
      newFilters[field] = value;
    }
    
    onFilterChange(newFilters);
  };

  const handleRemoveFilter = (field) => {
    const newFilters = { ...filters };
    delete newFilters[field];
    onFilterChange(newFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.isActive !== undefined) count++;
    if (filters.hasUser !== undefined) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <Box>
      <Box display="flex" gap={2} alignItems="center" mb={2} flexWrap="wrap">
        {/* Search field */}
        <TextField
          placeholder="Search employees..."
          value={searchText}
          onChange={handleSearchChange}
          size="small"
          sx={{ minWidth: 250, flex: '1 1 250px', '& .MuiInputBase-root': { height: 40 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchText && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        {/* Status filter */}
        <FormControl size="small" sx={{ minWidth: 120, flex: '0 1 auto', '& .MuiInputBase-root': { height: 40 } }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.isActive === true ? 'active' : filters.isActive === false ? 'inactive' : ''}
            onChange={(e) => handleFilterChange('isActive', 
              e.target.value === 'active' ? true : 
              e.target.value === 'inactive' ? false : undefined
            )}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>

        {/* RadixDash user filter */}
        <FormControl size="small" sx={{ minWidth: 150, flex: '0 1 auto', '& .MuiInputBase-root': { height: 40 } }}>
          <InputLabel>RadixDash User</InputLabel>
          <Select
            value={filters.hasUser === true ? 'yes' : filters.hasUser === false ? 'no' : ''}
            onChange={(e) => handleFilterChange('hasUser',
              e.target.value === 'yes' ? true :
              e.target.value === 'no' ? false : undefined
            )}
            label="RadixDash User"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Active filters display */}
      {activeFilterCount > 0 && (
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            Active filters:
          </Typography>
          
          {filters.search && (
            <Chip
              label={`Search: ${filters.search}`}
              size="small"
              onDelete={() => {
                setSearchText('');
                handleRemoveFilter('search');
              }}
            />
          )}
          
          {filters.isActive !== undefined && (
            <Chip
              label={`Status: ${filters.isActive ? 'Active' : 'Inactive'}`}
              size="small"
              onDelete={() => handleRemoveFilter('isActive')}
            />
          )}
          
          {filters.hasUser !== undefined && (
            <Chip
              label={`Access: ${filters.hasUser ? 'Has Access' : 'No Access'}`}
              size="small"
              onDelete={() => handleRemoveFilter('hasUser')}
            />
          )}
          
          {activeFilterCount > 1 && (
            <Chip
              label="Clear all"
              size="small"
              variant="outlined"
              onClick={() => {
                setSearchText('');
                onFilterChange({});
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

EmployeeFilters.propTypes = {
  onFilterChange: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    search: PropTypes.string,
    isActive: PropTypes.bool,
    hasUser: PropTypes.bool
  })
};

export default EmployeeFilters;